import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { addSimulated, removeSimulated } from "./simStore";
import { upsertCompanyTab, closeCompanyTab } from "./useCompanyOpenTabs";

const SimContext = createContext(null);

export function useSim() {
  const ctx = useContext(SimContext);
  if (!ctx) {
    return {
      simMode: false,
      setSimMode: () => {},
      onSimulate: () => {},
      clearSimTab: () => {},
      removeSimulatedEntry: () => {},
    };
  }
  return ctx;
}

export function SimProvider({ children }) {
  const [simMode, setSimMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const clearSimTab = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("simTab");
    const q = params.toString();
    navigate(`${location.pathname}${q ? `?${q}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const onSimulate = useCallback(
    ({ id, name, address, group }) => {
      const sid = String(id);
      addSimulated({ id: sid, name, address, group });
      upsertCompanyTab({
        id: `sim:${sid}`,
        name: name || sid,
        kind: String(group || "facility").toLowerCase(),
        to: `/company/${sid}`,
        sim: true,
      });
      const params = new URLSearchParams(location.search);
      params.set("simTab", `sim:${sid}`);
      navigate(`${location.pathname}?${params.toString()}`, { replace: false });
    },
    [location.pathname, location.search, navigate]
  );

  const value = useMemo(
    () => ({
      simMode,
      setSimMode,
      onSimulate,
      clearSimTab,
      /** When closing a sim tab from the tab strip */
      removeSimulatedEntry: (tabId) => {
        const raw = String(tabId || "").replace(/^sim:/, "");
        removeSimulated(raw);
        closeCompanyTab(`sim:${raw}`);
      },
    }),
    [simMode, onSimulate, clearSimTab]
  );

  return <SimContext.Provider value={value}>{children}</SimContext.Provider>;
}
